import { useEffect, useRef } from "react";
import "./Backdrop.css";

const GAP = 34; // distance between dots
const REACH = 230; // how far the pointer's influence goes
const IDLE_MS = 3500; // after this long without the pointer, it wanders by itself
const RIPPLE_SPEED = 520; // px per second
const RIPPLE_LIFE = 1.6; // seconds

// A field of dots behind the whole page. It breathes in slow waves, leans
// towards the pointer, and sends a ripple out from every click or tap.
const Backdrop = ({ theme }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    // Read on the first frame: by then the new theme is on <html>
    let ink = "";
    let accent = "";

    let width = 0;
    let height = 0;
    let frame = 0;
    let last = performance.now();
    let lastMove = -Infinity;
    const pointer = { x: 0, y: 0 };
    const focus = { x: 0, y: 0 }; // where the field is "looking"; eases towards its target
    let ripples = [];

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const draw = (now) => {
      if (!ink) {
        const css = getComputedStyle(document.documentElement);
        ink = css.getPropertyValue("--ink").trim();
        accent = css.getPropertyValue("--accent-text").trim();
      }
      const t = now / 1000;
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;

      // Follow the pointer; when it has been still for a while, drift around
      const idle = now - lastMove > IDLE_MS;
      const targetX = idle ? width * (0.5 + 0.34 * Math.sin(t * 0.21)) : pointer.x;
      const targetY = idle ? height * (0.5 + 0.3 * Math.sin(t * 0.33 + 1.3)) : pointer.y;
      const ease = 1 - Math.exp(-dt * (idle ? 1.2 : 5));
      focus.x += (targetX - focus.x) * ease;
      focus.y += (targetY - focus.y) * ease;

      const breath = 0.5 + 0.5 * Math.sin(t * 0.9); // one breath every ~7s
      ripples = ripples.filter((r) => t - r.start < RIPPLE_LIFE);

      ctx.clearRect(0, 0, width, height);

      // Soft glow under the point of attention
      const glowR = 380 + 60 * breath;
      const glow = ctx.createRadialGradient(focus.x, focus.y, 0, focus.x, focus.y, glowR);
      glow.addColorStop(0, accent);
      glow.addColorStop(1, "transparent");
      ctx.globalAlpha = 0.07 + 0.05 * breath;
      ctx.fillStyle = glow;
      ctx.fillRect(focus.x - glowR, focus.y - glowR, glowR * 2, glowR * 2);

      // The grid slides a little with the page so scrolling moves it too
      const shift = -((window.scrollY * 0.18) % GAP);
      for (let gy = shift - GAP; gy < height + GAP; gy += GAP) {
        for (let gx = GAP / 2; gx < width + GAP; gx += GAP) {
          const dx = focus.x - gx;
          const dy = focus.y - gy;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const near = dist < REACH ? (1 - dist / REACH) ** 2 : 0;
          const wave = 0.5 + 0.5 * Math.sin(t * 1.3 - dist * 0.011);

          let ring = 0;
          for (const r of ripples) {
            const age = t - r.start;
            const d = Math.hypot(gx - r.x, gy - r.y) - age * RIPPLE_SPEED;
            ring += Math.exp(-(d * d) / 2200) * (1 - age / RIPPLE_LIFE);
          }
          if (ring > 1) ring = 1;

          const hot = Math.min(1, near + ring);
          const pull = near * 9;
          const x = gx + (dist ? (dx / dist) * pull : 0);
          const y = gy + (dist ? (dy / dist) * pull : 0);
          const radius = 1 + 0.9 * wave * (0.35 + 0.65 * breath) + 1.9 * near + 2.2 * ring;

          ctx.beginPath();
          ctx.arc(x, y, radius, 0, Math.PI * 2);
          ctx.globalAlpha = 0.13 + 0.13 * wave * breath;
          ctx.fillStyle = ink;
          ctx.fill();
          if (hot > 0.02) {
            ctx.globalAlpha = Math.min(0.75, hot * 0.8);
            ctx.fillStyle = accent;
            ctx.fill();
          }
        }
      }
      ctx.globalAlpha = 1;

      if (!reduced) frame = requestAnimationFrame(draw);
    };

    const onMove = (e) => {
      pointer.x = e.clientX;
      pointer.y = e.clientY;
      lastMove = performance.now();
    };
    const onDown = (e) => {
      onMove(e);
      if (!reduced) ripples.push({ x: e.clientX, y: e.clientY, start: performance.now() / 1000 });
    };
    const onResize = () => {
      resize();
      if (reduced) draw(performance.now());
    };

    resize();
    focus.x = width / 2;
    focus.y = height / 2;
    frame = requestAnimationFrame(draw);
    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerdown", onDown, { passive: true });
    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerdown", onDown);
      window.removeEventListener("resize", onResize);
    };
    // Colors come from the theme's CSS variables, so start over when it changes
  }, [theme]);

  return <canvas ref={canvasRef} className="backdrop" aria-hidden="true" />;
};

export default Backdrop;
